import type { SplatData } from '@/plugins/gsplat/core/SplatData'

/**
 * 导出选项
 */
export interface ExportOptions {
  /** 是否包含已删除的splat（默认：false） */
  includeDeleted?: boolean
  /** 删除掩码（0=有效，1=已删除） */
  deletedMask?: Uint8Array
  /** 导出的最大球谐阶数（0-3，默认：3） */
  maxSHBands?: 0 | 1 | 2 | 3
  /** 导出格式（默认：'ply'） */
  format?: 'ply' | 'splat'
}

/**
 * Splat 数据导出器
 *
 * 支持导出为标准PLY格式（3DGS标准格式）、.splat格式（简化格式）和压缩PLY格式。
 *
 * 使用方式：
 * ```typescript
 * const exporter = new SplatExporter();
 *
 * // 导出为标准PLY
 * const plyBlob = await exporter.exportToPly(splatData, {
 *   maxSHBands: 3,
 *   deletedMask: cropper.getDeletedMask()
 * });
 *
 * // 导出为压缩PLY
 * const compressedPlyBlob = await exporter.exportToCompressedPly(splatData);
 *
 * // 导出为.splat
 * const splatBlob = await exporter.exportToSplat(splatData);
 * ```
 */
export class SplatExporter {
  /**
   * 导出为标准PLY格式（3DGS通用格式）
   *
   * PLY格式包含：
   * - 位置（x, y, z）
   * - 旋转四元数（rot_0, rot_1, rot_2, rot_3）
   * - 缩放（scale_0, scale_1, scale_2）
   * - 球谐系数（f_dc_0/1/2 + f_rest_*）
   * - 不透明度（opacity）
   *
   * @param splatData Splat数据
   * @param options 导出选项
   * @returns PLY文件的Blob对象
   */
  async exportPly(splatData: SplatData, options: ExportOptions = {}): Promise<Blob> {
    const { includeDeleted = false, deletedMask, maxSHBands = 3 } = options

    // 1. 统计有效splat数量
    let count = 0
    const validIndices: number[] = []

    for (let i = 0; i < splatData.numSplats; i++) {
      if (includeDeleted || !deletedMask || !deletedMask[i]) {
        validIndices.push(i)
        count++
      }
    }

    // 2. 构建PLY Header
    const header = this.buildPlyHeader(splatData, count, maxSHBands)

    // 3. 计算每个splat的字节数
    const bytesPerSplat = this.calcBytesPerSplat(splatData, maxSHBands)

    // 4. 写入Binary Body
    const body = new ArrayBuffer(count * bytesPerSplat)
    const view = new DataView(body)

    let offset = 0
    for (const i of validIndices) {
      offset = this.writeSplat(view, offset, splatData, i, maxSHBands)
    }

    // 5. 合并Header和Body
    const headerBytes = new TextEncoder().encode(header)
    const result = new Uint8Array(headerBytes.length + body.byteLength)
    result.set(headerBytes)
    result.set(new Uint8Array(body), headerBytes.length)

    return new Blob([result], { type: 'application/octet-stream' })
  }

  /**
   * 导出为.splat格式（简化格式）
   *
   * .splat格式是一种简化的二进制格式，每个splat固定32字节：
   * - 位置（3 × float32 = 12字节）
   * - 缩放（3 × float32 = 12字节，已转换为exp(scale)）
   * - 颜色（4 × uint8 = 4字节，RGBA）
   * - 旋转（4 × uint8 = 4字节，四元数量化）
   *
   * @param splatData Splat数据
   * @param options 导出选项
   * @returns .splat文件的Blob对象
   */
  async exportSplat(splatData: SplatData, options: ExportOptions = {}): Promise<Blob> {
    const { includeDeleted = false, deletedMask } = options

    // 1. 统计有效splat
    const validIndices: number[] = []
    for (let i = 0; i < splatData.numSplats; i++) {
      if (includeDeleted || !deletedMask || !deletedMask[i]) {
        validIndices.push(i)
      }
    }

    // 2. 获取属性数组
    const x = splatData.getProp('x') as Float32Array
    const y = splatData.getProp('y') as Float32Array
    const z = splatData.getProp('z') as Float32Array
    const scale0 = splatData.getProp('scale_0') as Float32Array
    const scale1 = splatData.getProp('scale_1') as Float32Array
    const scale2 = splatData.getProp('scale_2') as Float32Array
    const rot0 = splatData.getProp('rot_0') as Float32Array
    const rot1 = splatData.getProp('rot_1') as Float32Array
    const rot2 = splatData.getProp('rot_2') as Float32Array
    const rot3 = splatData.getProp('rot_3') as Float32Array
    const f_dc_0 = splatData.getProp('f_dc_0') as Float32Array
    const f_dc_1 = splatData.getProp('f_dc_1') as Float32Array
    const f_dc_2 = splatData.getProp('f_dc_2') as Float32Array
    const opacity = splatData.getProp('opacity') as Float32Array

    // 球谐系数转换常量
    const SH_C0 = 0.28209479177387814

    // 3. .splat格式：每个splat 32字节
    const buffer = new ArrayBuffer(validIndices.length * 32)
    const view = new DataView(buffer)

    let offset = 0
    for (const i of validIndices) {
      // 位置（12字节）
      view.setFloat32(offset, x[i], true)
      offset += 4
      view.setFloat32(offset, y[i], true)
      offset += 4
      view.setFloat32(offset, z[i], true)
      offset += 4

      // 缩放（12字节）- 转换为实际缩放值
      view.setFloat32(offset, Math.exp(scale0[i]), true)
      offset += 4
      view.setFloat32(offset, Math.exp(scale1[i]), true)
      offset += 4
      view.setFloat32(offset, Math.exp(scale2[i]), true)
      offset += 4

      // 颜色（4字节）- SH DC转RGB + sigmoid opacity
      const r = Math.max(0, Math.min(255, Math.round((0.5 + f_dc_0[i] * SH_C0) * 255)))
      const g = Math.max(0, Math.min(255, Math.round((0.5 + f_dc_1[i] * SH_C0) * 255)))
      const b = Math.max(0, Math.min(255, Math.round((0.5 + f_dc_2[i] * SH_C0) * 255)))
      const a = Math.max(0, Math.min(255, Math.round((1 / (1 + Math.exp(-opacity[i]))) * 255)))

      view.setUint8(offset++, r)
      view.setUint8(offset++, g)
      view.setUint8(offset++, b)
      view.setUint8(offset++, a)

      // 旋转（4字节）- 四元数归一化并量化到uint8
      const qLen = Math.sqrt(rot0[i] ** 2 + rot1[i] ** 2 + rot2[i] ** 2 + rot3[i] ** 2)
      const qw = rot0[i] / qLen
      const qx = rot1[i] / qLen
      const qy = rot2[i] / qLen
      const qz = rot3[i] / qLen

      view.setUint8(offset++, Math.round((qw * 0.5 + 0.5) * 255))
      view.setUint8(offset++, Math.round((qx * 0.5 + 0.5) * 255))
      view.setUint8(offset++, Math.round((qy * 0.5 + 0.5) * 255))
      view.setUint8(offset++, Math.round((qz * 0.5 + 0.5) * 255))
    }

    return new Blob([buffer], { type: 'application/octet-stream' })
  }

  /**
   * 导出为压缩PLY格式
   *
   * 压缩格式使用 chunk-based 量化：
   * - Chunk: 每256个splat一个chunk，存储min/max范围
   * - Vertex: 4个uint32打包数据
   *
   * @param splatData Splat数据
   * @param options 导出选项
   * @returns 压缩PLY文件的Blob对象
   */
  async exportCompressedPly(splatData: SplatData, options: ExportOptions = {}): Promise<Blob> {
    const { includeDeleted = false, deletedMask } = options
    // Note: maxSHBands not yet supported for compressed export

    // 1. 统计有效splat
    const validIndices: number[] = []
    for (let i = 0; i < splatData.numSplats; i++) {
      if (includeDeleted || !deletedMask || !deletedMask[i]) {
        validIndices.push(i)
      }
    }

    const numSplats = validIndices.length
    const numChunks = Math.ceil(numSplats / 256)

    // 2. 获取属性数组
    const x = splatData.getProp('x') as Float32Array
    const y = splatData.getProp('y') as Float32Array
    const z = splatData.getProp('z') as Float32Array
    const scale0 = splatData.getProp('scale_0') as Float32Array
    const scale1 = splatData.getProp('scale_1') as Float32Array
    const scale2 = splatData.getProp('scale_2') as Float32Array
    const rot0 = splatData.getProp('rot_0') as Float32Array
    const rot1 = splatData.getProp('rot_1') as Float32Array
    const rot2 = splatData.getProp('rot_2') as Float32Array
    const rot3 = splatData.getProp('rot_3') as Float32Array
    const f_dc_0 = splatData.getProp('f_dc_0') as Float32Array
    const f_dc_1 = splatData.getProp('f_dc_1') as Float32Array
    const f_dc_2 = splatData.getProp('f_dc_2') as Float32Array
    const opacity = splatData.getProp('opacity') as Float32Array

    const SH_C0 = 0.28209479177387814

    // 3. 计算每个chunk的min/max范围
    const chunkSize = 12 // 基础：min_xyz + max_xyz + min_scale + max_scale
    const chunkData = new Float32Array(numChunks * chunkSize)

    for (let c = 0; c < numChunks; c++) {
      const startIdx = c * 256
      const endIdx = Math.min(startIdx + 256, numSplats)

      let minX = Infinity,
        minY = Infinity,
        minZ = Infinity
      let maxX = -Infinity,
        maxY = -Infinity,
        maxZ = -Infinity
      let minSX = Infinity,
        minSY = Infinity,
        minSZ = Infinity
      let maxSX = -Infinity,
        maxSY = -Infinity,
        maxSZ = -Infinity

      for (let j = startIdx; j < endIdx; j++) {
        const i = validIndices[j]
        minX = Math.min(minX, x[i])
        maxX = Math.max(maxX, x[i])
        minY = Math.min(minY, y[i])
        maxY = Math.max(maxY, y[i])
        minZ = Math.min(minZ, z[i])
        maxZ = Math.max(maxZ, z[i])
        minSX = Math.min(minSX, scale0[i])
        maxSX = Math.max(maxSX, scale0[i])
        minSY = Math.min(minSY, scale1[i])
        maxSY = Math.max(maxSY, scale1[i])
        minSZ = Math.min(minSZ, scale2[i])
        maxSZ = Math.max(maxSZ, scale2[i])
      }

      const off = c * chunkSize
      chunkData[off + 0] = minX
      chunkData[off + 1] = minY
      chunkData[off + 2] = minZ
      chunkData[off + 3] = maxX
      chunkData[off + 4] = maxY
      chunkData[off + 5] = maxZ
      chunkData[off + 6] = minSX
      chunkData[off + 7] = minSY
      chunkData[off + 8] = minSZ
      chunkData[off + 9] = maxSX
      chunkData[off + 10] = maxSY
      chunkData[off + 11] = maxSZ
    }

    // 4. 打包顶点数据
    const vertexData = new Uint32Array(numSplats * 4)

    for (let j = 0; j < numSplats; j++) {
      const i = validIndices[j]
      const chunkIdx = Math.floor(j / 256)
      const off = chunkIdx * chunkSize

      // 位置量化 (11-10-11 bits)
      const px = this.quantize(x[i], chunkData[off + 0], chunkData[off + 3], 2047)
      const py = this.quantize(y[i], chunkData[off + 1], chunkData[off + 4], 1023)
      const pz = this.quantize(z[i], chunkData[off + 2], chunkData[off + 5], 2047)
      vertexData[j * 4 + 0] = (px << 21) | (py << 11) | pz

      // 旋转量化 (2-10-10-10 bits)
      vertexData[j * 4 + 1] = this.packRotation(rot0[i], rot1[i], rot2[i], rot3[i])

      // 缩放量化 (11-10-11 bits)
      const sx = this.quantize(scale0[i], chunkData[off + 6], chunkData[off + 9], 2047)
      const sy = this.quantize(scale1[i], chunkData[off + 7], chunkData[off + 10], 1023)
      const sz = this.quantize(scale2[i], chunkData[off + 8], chunkData[off + 11], 2047)
      vertexData[j * 4 + 2] = (sx << 21) | (sy << 11) | sz

      // 颜色量化 (8-8-8-8 bits)
      const r = Math.max(0, Math.min(255, Math.round((0.5 + f_dc_0[i] * SH_C0) * 255)))
      const g = Math.max(0, Math.min(255, Math.round((0.5 + f_dc_1[i] * SH_C0) * 255)))
      const b = Math.max(0, Math.min(255, Math.round((0.5 + f_dc_2[i] * SH_C0) * 255)))
      const a = Math.max(0, Math.min(255, Math.round((1 / (1 + Math.exp(-opacity[i]))) * 255)))
      vertexData[j * 4 + 3] = (r << 24) | (g << 16) | (b << 8) | a
    }

    // 5. 构建压缩PLY Header
    let header = 'ply\nformat binary_little_endian 1.0\n'
    header += `comment Compressed PLY exported by ThreeGS\n`
    header += `element chunk ${numChunks}\n`
    header += 'property float min_x\nproperty float min_y\nproperty float min_z\n'
    header += 'property float max_x\nproperty float max_y\nproperty float max_z\n'
    header += 'property float min_scale_x\nproperty float min_scale_y\nproperty float min_scale_z\n'
    header += 'property float max_scale_x\nproperty float max_scale_y\nproperty float max_scale_z\n'
    header += `element vertex ${numSplats}\n`
    header += 'property uint packed_position\n'
    header += 'property uint packed_rotation\n'
    header += 'property uint packed_scale\n'
    header += 'property uint packed_color\n'
    header += 'end_header\n'

    // 6. 写入二进制数据
    const headerBytes = new TextEncoder().encode(header)
    const chunkBytes = new Uint8Array(chunkData.buffer)
    const vertexBytes = new Uint8Array(vertexData.buffer)

    const result = new Uint8Array(headerBytes.length + chunkBytes.length + vertexBytes.length)
    result.set(headerBytes, 0)
    result.set(chunkBytes, headerBytes.length)
    result.set(vertexBytes, headerBytes.length + chunkBytes.length)

    return new Blob([result], { type: 'application/octet-stream' })
  }

  /**
   * 量化值到指定范围
   */
  private quantize(value: number, min: number, max: number, maxInt: number): number {
    if (max === min) return 0
    const t = (value - min) / (max - min)
    return Math.max(0, Math.min(maxInt, Math.round(t * maxInt)))
  }

  /**
   * 打包旋转四元数 (2-10-10-10 bits)
   * 2 bits: 最大元素索引
   * 10-10-10 bits: 其他三个元素
   */
  private packRotation(w: number, x: number, y: number, z: number): number {
    // 归一化
    const len = Math.sqrt(w * w + x * x + y * y + z * z)
    if (len > 0) {
      w /= len
      x /= len
      y /= len
      z /= len
    }

    // 找到最大元素
    const absW = Math.abs(w),
      absX = Math.abs(x),
      absY = Math.abs(y),
      absZ = Math.abs(z)
    let maxIdx = 0
    let maxVal = absW
    if (absX > maxVal) {
      maxIdx = 1
      maxVal = absX
    }
    if (absY > maxVal) {
      maxIdx = 2
      maxVal = absY
    }
    if (absZ > maxVal) {
      maxIdx = 3
    }

    // 确保最大元素为正
    const sign = [w, x, y, z][maxIdx] >= 0 ? 1 : -1
    w *= sign
    x *= sign
    y *= sign
    z *= sign

    // 获取三个非最大元素
    let a: number, b: number, c: number
    switch (maxIdx) {
      case 0:
        a = x
        b = y
        c = z
        break
      case 1:
        a = w
        b = y
        c = z
        break
      case 2:
        a = x
        b = w
        c = z
        break
      case 3:
        a = x
        b = y
        c = w
        break
      default:
        a = x
        b = y
        c = z
    }

    // 量化到 10 bits (范围 [-1/sqrt(2), 1/sqrt(2)] -> [0, 1023])
    const norm = Math.SQRT2
    const qa = Math.max(0, Math.min(1023, Math.round((a / norm + 0.5) * 1023)))
    const qb = Math.max(0, Math.min(1023, Math.round((b / norm + 0.5) * 1023)))
    const qc = Math.max(0, Math.min(1023, Math.round((c / norm + 0.5) * 1023)))

    return (maxIdx << 30) | (qa << 20) | (qb << 10) | qc
  }

  /**
   * 构建PLY文件头
   */
  private buildPlyHeader(_splatData: SplatData, count: number, maxSHBands: number): string {
    let header = 'ply\nformat binary_little_endian 1.0\n'
    header += `element vertex ${count}\n`

    // 基础属性（位置、旋转、缩放、DC颜色、不透明度）
    header += 'property float x\n'
    header += 'property float y\n'
    header += 'property float z\n'
    header += 'property float rot_0\n'
    header += 'property float rot_1\n'
    header += 'property float rot_2\n'
    header += 'property float rot_3\n'
    header += 'property float scale_0\n'
    header += 'property float scale_1\n'
    header += 'property float scale_2\n'
    header += 'property float f_dc_0\n'
    header += 'property float f_dc_1\n'
    header += 'property float f_dc_2\n'
    header += 'property float opacity\n'

    // 球谐系数（f_rest_*）
    if (maxSHBands > 0) {
      const shCoeffCount = this.getSHCoeffCount(maxSHBands)
      for (let i = 0; i < shCoeffCount; i++) {
        header += `property float f_rest_${i}\n`
      }
    }

    header += 'end_header\n'
    return header
  }

  /**
   * 获取球谐系数数量（不包括DC）
   */
  private getSHCoeffCount(bands: number): number {
    // bands 0: 0 coeffs (仅DC)
    // bands 1: 9 coeffs (DC + 1阶，每个颜色通道3个)
    // bands 2: 24 coeffs (DC + 1-2阶，每个颜色通道8个)
    // bands 3: 45 coeffs (DC + 1-3阶，每个颜色通道15个)
    const counts = [0, 9, 24, 45]
    return counts[bands] || 0
  }

  /**
   * 计算每个splat的字节数
   */
  private calcBytesPerSplat(_splatData: SplatData, maxSHBands: number): number {
    // 基础：14个float属性（位置3 + 旋转4 + 缩放3 + DC颜色3 + 不透明度1）
    let bytes = 14 * 4

    // 球谐系数
    if (maxSHBands > 0) {
      bytes += this.getSHCoeffCount(maxSHBands) * 4
    }

    return bytes
  }

  /**
   * 写入单个splat到DataView
   */
  private writeSplat(
    view: DataView,
    offset: number,
    splatData: SplatData,
    index: number,
    maxSHBands: number
  ): number {
    // 基础属性列表
    const props = [
      'x',
      'y',
      'z',
      'rot_0',
      'rot_1',
      'rot_2',
      'rot_3',
      'scale_0',
      'scale_1',
      'scale_2',
      'f_dc_0',
      'f_dc_1',
      'f_dc_2',
      'opacity',
    ]

    // 写入基础属性
    for (const prop of props) {
      const arr = splatData.getProp(prop) as Float32Array
      if (arr) {
        view.setFloat32(offset, arr[index], true)
      } else {
        view.setFloat32(offset, 0, true)
      }
      offset += 4
    }

    // 写入球谐系数（f_rest_*）
    if (maxSHBands > 0) {
      const shCount = this.getSHCoeffCount(maxSHBands)
      for (let i = 0; i < shCount; i++) {
        const arr = splatData.getProp(`f_rest_${i}`) as Float32Array
        if (arr) {
          view.setFloat32(offset, arr[index], true)
        } else {
          view.setFloat32(offset, 0, true)
        }
        offset += 4
      }
    }

    return offset
  }
}

/**
 * 便捷导出函数：将 SplatData 导出为 PLY 格式。
 *
 * @param splatData - 待导出的原始 splat 数据
 * @param options - 可选导出参数，控制 SH 阶数、裁剪 mask 等
 * @returns 包含 PLY 字节流的 Blob
 */
export async function exportToPly(splatData: SplatData, options?: ExportOptions): Promise<Blob> {
  const exporter = new SplatExporter()
  return exporter.exportPly(splatData, options)
}

/**
 * 便捷导出函数：将 SplatData 导出为 .splat 格式。
 *
 * @param splatData - 待导出的原始 splat 数据
 * @param options - 可选导出参数
 * @returns 包含 .splat 字节流的 Blob
 */
export async function exportToSplat(splatData: SplatData, options?: ExportOptions): Promise<Blob> {
  const exporter = new SplatExporter()
  return exporter.exportSplat(splatData, options)
}

/**
 * 便捷导出函数：将 SplatData 导出为压缩 PLY 格式。
 *
 * @param splatData - 待导出的原始 splat 数据
 * @param options - 可选导出参数
 * @returns 包含压缩 PLY 字节流的 Blob
 */
export async function exportToCompressedPly(
  splatData: SplatData,
  options?: ExportOptions
): Promise<Blob> {
  const exporter = new SplatExporter()
  return exporter.exportCompressedPly(splatData, options)
}
